import { create } from "zustand";
import { API_BASE_URL } from "../lib/config";

export interface AuthUser {
  staffId: string;
  email: string;
  name: string;
  role: string;
  practiceId: string;
}

interface AuthStore {
  token: string | null;
  user: AuthUser | null;
  status: "idle" | "loading" | "ready";
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  hydrate: () => Promise<void>;
}

const TOKEN_KEY = "fc.token";

export const useAuthStore = create<AuthStore>((set, get) => ({
  token: null,
  user: null,
  status: "idle",

  login: async (email, password) => {
    const res = await fetch(`${API_BASE_URL}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      let msg = "Sign-in failed";
      try {
        const parsed = JSON.parse(text);
        if (parsed?.error) msg = parsed.error;
      } catch {
        if (text) msg = text;
      }
      throw new Error(msg);
    }
    const { token, user } = (await res.json()) as { token: string; user: AuthUser };
    localStorage.setItem(TOKEN_KEY, token);
    set({ token, user, status: "ready" });
  },

  logout: async () => {
    const token = get().token;
    if (token) {
      try {
        await fetch(`${API_BASE_URL}/auth/logout`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {
        // best-effort; we're tearing down regardless
      }
    }
    localStorage.removeItem(TOKEN_KEY);
    set({ token: null, user: null, status: "ready" });
  },

  hydrate: async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      set({ status: "ready" });
      return;
    }
    set({ token, status: "loading" });
    try {
      const res = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`me ${res.status}`);
      const user = (await res.json()) as AuthUser;
      set({ user, status: "ready" });
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      set({ token: null, user: null, status: "ready" });
    }
  },
}));
